import { useDropzone } from 'react-dropzone';
import { Autocomplete, Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import type { GetUsersResponse } from '@weft/api-definition';
import { VyIcon } from '@weft/icons';
import type { AccountInput, Contact, NewAccount } from '@weft/types';
import { isNotEmptyString, withDefault, withNewOptionalValue } from '@weft/utils';
import { countries } from 'countries-list';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { CountrySelect } from '../../../../../../components/new-design/inputs/CountrySelect';

export const AccountInfoSection: React.FC = () => {
  const queryClient = useQueryClient();
  const usersResponse = queryClient.getQueryData<GetUsersResponse>('users');
  const users = withDefault(usersResponse?.users, []);

  const { control, register, setValue, watch, reset, formState: { defaultValues, isSubmitting } } = useFormContext<AccountInput, unknown, NewAccount>();

  const registerCompanyName = register('name');
  const registerMainContact = register('contact');
  const registerCompanyLogo = register('logoUrl');
  const registerStreetAddress = register('settings.address.addressText');
  const registerCity = register('settings.address.city');
  const registerState = register('settings.address.state');
  const registerPostalCode = register('settings.address.postalCode');
  const registerCountry = register('settings.address.country');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: ([file]) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        setValue(registerCompanyLogo.name, base64);
      };
      reader.readAsDataURL(file);
    },
  });

  const handleCancel = () => {
    reset();
  };

  return (
    <Stack
      divider={<Divider sx={{ marginY: '20px' }}/>}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        display="flex"
        paddingX="20px"
      >
        <Stack>
          <Typography variant="h6">Account Info</Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
          >
            Update your personal details here
          </Typography>
        </Stack>

        <Stack
          direction="row"
          columnGap="16px"
          alignItems="start"
        >
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            type="submit"
            size="large"
            disabled={isSubmitting}
          >
            Save
          </Button>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        maxWidth="md"
        display="flex"
        paddingX="20px"
      >
        <Typography
          display="flex"
          flex={7}
        >
          Company name
        </Typography>

        <Box sx={{
          display: 'flex',
          flex: 8,
        }}
        >
          <Controller
            control={control}
            name={registerCompanyName.name}
            defaultValue={defaultValues?.name ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                {...field}
              />
            )}
          />
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        maxWidth="md"
        display="flex"
        paddingX="20px"
      >
        <Typography
          display="flex"
          flex={7}
        >
          Main contact
        </Typography>

        <Box sx={{
          display: 'flex',
          flex: 8,
        }}
        >
          <Controller
            control={control}
            name={registerMainContact.name}
            defaultValue={{
              ...defaultValues?.contact,
              email: defaultValues?.contact?.email ?? '',
            }}
            render={({ field }) => (
              <Autocomplete
                fullWidth
                autoHighlight
                getOptionLabel={(option) => option.email ?? ''}
                size="small"
                options={users}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...field}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password',
                    }}
                  />
                )}
                value={users.find((user) => user.email === field.value?.email) ?? null}
                onChange={(event, newValue) => {
                  event.preventDefault();
                  
                  const contact: Contact | null = withNewOptionalValue(newValue, ({ email }) => ({
                    email,
                  }), null);
                  
                  field.onChange?.(contact);
                }}
              />
            )}
          />
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        maxWidth="md"
        display="flex"
        paddingX="20px"
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 7,
        }}
        >
          <Typography >
            Company Logo
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
          >
            This will be displayed on your invoices
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          flex: 8,
          columnGap: '32px',
        }}
        >
          {isNotEmptyString(watch(registerCompanyLogo.name)) && (
            <div className="flex w-max h-max">
              <img
                src={watch(registerCompanyLogo.name)}
                alt="Company logo"
                width={40}
                height={40}
              />
            </div>
          )}

          <Box
            {...getRootProps()}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              border: isDragActive ? '1px solid' : '1px dashed',
              borderColor: 'primary.main',
              borderRadius: '4px',
              padding: '30px',
              cursor: 'pointer',
              height: '100%',
            }}
          >
            <input {...getInputProps()}/>

            <VyIcon icon="Upload"/>

            {isDragActive && (
              <Typography
                color="primary"
              >
                Drop the files here
              </Typography>
            )}

            {!isDragActive && (
              <Box
                display="flex"
                columnGap="2px"
              >
                <Typography color="primary">Click to upload</Typography>
                <Typography>or drag and drop</Typography>
              </Box>
            )}

            <Typography
              variant="caption"
              color="text.secondary"
            >
              SVG, PNG, or JPG
            </Typography>
          </Box>
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        maxWidth="md"
        paddingX="20px"
      >
        <Typography
          display="flex"
          flex={7}
        >
          Street address
        </Typography>

        <Box sx={{
          display: 'flex',
          flex: 8,
        }}
        >
          <Controller
            control={control}
            name={registerStreetAddress.name}
            defaultValue={defaultValues?.settings?.address?.addressText ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                {...field}
              />
            )}
          />
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        maxWidth="md"
        paddingX="20px"
      >
        <Typography
          display="flex"
          flex={7}
        >
          City
        </Typography>

        <Box sx={{
          display: 'flex',
          flex: 8,
        }}
        >
          <Controller
            control={control}
            name={registerCity.name}
            defaultValue={defaultValues?.settings?.address?.city ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                {...field}
              />
            )}
          />
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        maxWidth="md"
        paddingX="20px"
      >
        <Typography
          display="flex"
          flex={7}
        >
          State / Province
        </Typography>

        <Box sx={{
          display: 'flex',
          flex: 8,
          columnGap: '16px',
        }}
        >
          <Controller
            control={control}
            name={registerState.name}
            defaultValue={defaultValues?.settings?.address?.state ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                placeholder="State"
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name={registerPostalCode.name}
            defaultValue={defaultValues?.settings?.address?.postalCode ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                placeholder="Postal code"
                {...field}
              />
            )}
          />
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        maxWidth="md"
        paddingX="20px"
      >
        <Typography
          display="flex"
          flex={7}
        >
          Country
        </Typography>

        <Box sx={{
          display: 'flex',
          flex: 8,
        }}
        >
          <Controller
            control={control}
            name={registerCountry.name}
            defaultValue={defaultValues?.settings?.address?.country ?? ''}
            render={({ field }) => (
              <CountrySelect
                value={getCountryByName(field.value ?? '')}
                textFieldProps={field}
                onChange={(country) => {
                  field.onChange(country?.name);
                }}
              />
            )}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

function getCountryByName(countryName: string) {
  return Object.values(countries).find((country) => country.name === countryName) ?? null;
}